import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Paper, Grid, Card, CardContent, Alert, LinearProgress, TextField } from '@mui/material';
import { Timeline as PipelineIcon, PlayArrow as RunIcon, ArrowForward as ArrowIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const API_BASE = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api'}/website-finder`;

interface PipelineStats {
  totalCompanies: number;
  missingWebsite: number;
  remainingToSearch: number;
  pending: number;
  approved: number;
  rejected: number;
  notFound: number;
  tagged: number;
  hasWebsiteUntagged: number;
}

// The pipeline, in order: ingest -> find website (batched) -> tag -> (later) contacts/send.
// Each stage here links to the page that actually does the work; this page is just the
// at-a-glance status + the "run the next batch" trigger, so nobody has to remember to
// hop between three separate pages or guess what's already been done.
const PipelinePage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<PipelineStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [batchSize, setBatchSize] = useState(15);
  const [runSummary, setRunSummary] = useState<any>(null);

  const token = () => localStorage.getItem('ems_access_token') || localStorage.getItem('token');
  const authFetch = (path: string, options: RequestInit = {}) => fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
      ...options.headers
    }
  });

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await authFetch('/stats');
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error);
      setStats(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runNextBatch = async () => {
    try {
      setRunning(true);
      setError(null);
      setRunSummary(null);
      const res = await authFetch('/run', { method: 'POST', body: JSON.stringify({ limit: batchSize }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error);
      setRunSummary(data);
      await loadStats();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  };

  const stages = stats ? [
    {
      label: 'Imported',
      value: stats.totalCompanies,
      hint: 'Total companies in the database',
      color: '#0f172a'
    },
    {
      label: 'Not yet searched',
      value: stats.remainingToSearch,
      hint: 'Missing a website, never queried',
      color: '#3b82f6',
      action: stats.remainingToSearch > 0 ? { label: 'Run next batch below', onClick: () => {} } : undefined
    },
    {
      label: 'Found, needs review',
      value: stats.pending,
      hint: 'Website found — approve + tag it',
      color: '#f59e0b',
      action: { label: 'Review now', onClick: () => navigate('/website-finder') }
    },
    {
      label: 'Has website, untagged',
      value: stats.hasWebsiteUntagged,
      hint: 'Including companies that already had a site before Website Finder ran',
      color: '#ef4444',
      action: stats.hasWebsiteUntagged > 0 ? { label: 'Tag in Database', onClick: () => navigate('/database') } : undefined
    },
    {
      label: 'Tagged',
      value: stats.tagged,
      hint: 'Ready for the (later) contact/email stage',
      color: '#10b981'
    }
  ] : [];

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <PipelineIcon sx={{ color: '#3b82f6' }} /> Pipeline
      </Typography>
      <Typography variant="body2" sx={{ color: '#475569', mb: 3 }}>
        Ingest → find website (batched, never re-searched) → tag. One place to see where things stand and run the next step.
        Contact/email lists come later, once tagged companies have real contact-person emails.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading && !stats && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stages.map((s, i) => (
          <React.Fragment key={s.label}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card variant="outlined" sx={{ borderRadius: '10px', height: '100%' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>{s.label}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: s.color, my: 0.5 }}>{s.value}</Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: s.action ? 1 : 0 }}>{s.hint}</Typography>
                  {s.action && (
                    <Button size="small" onClick={s.action.onClick} sx={{ textTransform: 'none', fontWeight: 600, p: 0, minWidth: 0 }}>
                      {s.action.label}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
            {i < stages.length - 1 && (
              <Grid item sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                <ArrowIcon sx={{ color: '#cbd5e1' }} />
              </Grid>
            )}
          </React.Fragment>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>Run next batch</Typography>
        <TextField
          label="Batch size"
          type="number"
          size="small"
          value={batchSize}
          onChange={(e) => setBatchSize(Math.min(Math.max(parseInt(e.target.value, 10) || 1, 1), 50))}
          sx={{ width: 120 }}
        />
        <Button
          variant="contained"
          startIcon={<RunIcon />}
          onClick={runNextBatch}
          disabled={running || (stats ? stats.remainingToSearch === 0 : false)}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 3 }}
        >
          {running ? 'Searching...' : 'Find websites for the next batch'}
        </Button>
        {stats && stats.remainingToSearch === 0 && (
          <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600 }}>
            Every company with a missing website has already been searched.
          </Typography>
        )}
        {running && <Typography variant="caption" sx={{ color: '#64748b' }}>One search per company — this can take a minute.</Typography>}
        {runSummary && !running && (
          <Typography variant="body2" sx={{ color: '#0f172a', width: '100%' }}>
            Found {runSummary.found}, not found {runSummary.notFound}, errors {runSummary.errors} (of {runSummary.processed}).{' '}
            {runSummary.found > 0 && (
              <Button size="small" onClick={() => navigate('/website-finder')} sx={{ textTransform: 'none', fontWeight: 600 }}>
                Review and tag now
              </Button>
            )}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default PipelinePage;
